import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import TableauVisualization from './tableau-visualization';
import graphicUrl from '../assets/us-states-by-archetype.jpg';
import './article.css';

const ArticleDetails = {
  articleLink: 'https://journals.aom.org/doi/10.5465/amd.2018.0014',
  title: 'Varieties of Local Government Experimentation',
  subtitle:
    'U.S. State-led Technology-Based Economic Development Policies, 2000–2015',
  datePublished: '8 March 2020',
  doi: 'DOI 10.5465/amd.2018.0014',
  doiLink: 'https://doi.org/10.5465/amd.2018.0014'
};

const getMobile = () => window.innerWidth <= 527;

export default function Article() {
  const [isMobile, setMobile] = useState(getMobile());
  useEffect(() => {
    function handleResize() {
      const nextMobile = getMobile();
      if (nextMobile !== isMobile) {
        setMobile(nextMobile);
      }
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
  useEffect(() => {
    if (window.tableau) {
      window.tableau.vizs.forEach(v => {
        v.refresh();
      });
    }
  }, [isMobile]);

  return (
    <article className="Article">
      <a
        className="Article-headerReturnLink container wide"
        href={ArticleDetails.articleLink}
      >
        Return to Academy of Management Discoveries
      </a>
      <header className="Article-header container wide">
        <h4 className="Article-notes">
          {ArticleDetails.datePublished} <span>&middot;</span>{' '}
          <a href={ArticleDetails.doiLink}>{ArticleDetails.doi}</a>
        </h4>
        <h1 className="Article-title">{ArticleDetails.title}</h1>
        <h2 className="Article-subtitle">{ArticleDetails.subtitle}</h2>
        <h3 className="Article-authors">
          By <span>Daniel Erian Armanios</span>, <span>Lauren Lanahan</span>,
          and <span>Dian Yu</span>
        </h3>
      </header>
      <div className="container">
        <p className="Article-notice">
          This web page is a supplement to the above article which can be read
          at the{' '}
          <a href={ArticleDetails.articleLink}>
            Academy of Management Discoveries
          </a>
          .
        </p>
      </div>
      <div className="Article-content">
        <p>
          A recent understudied phenomenon has emerged whereby public
          institutions are collectively taking on an expanded role in
          technological innovation by offering resource, policy, and
          infrastructure support even when the trajectories of these
          technologies are unclear. We trace this phenomenon of state government
          experimentation in the arena of{' '}
          <strong>technology-based economic development (TBED)</strong> from
          2000–2015.
        </p>
        <p>
          We rely on the State Science and Technology Institute as the primary
          data source. To explore the raw data underlying this work and
          subsequent visualizations, visit the{' '}
          <a href="https://www.cmu.edu/epp/stategovexp/">
            CMU State Goverment Data webpage
          </a>
          .
        </p>
        <h3>Annual Policy Counts from 2000–2015</h3>
        <figure>
          <TableauVisualization
            id="viz1591836691823"
            path="AnnucalPolicyTotals/Dashboard1"
            placeholderImagePath="An/AnnucalPolicyTotals/Dashboard1/1.png"
            width={1000}
            height={827}
            minHeight={727}
            isMobile={isMobile}
            loadOrder={1}
          />
        </figure>
        <p>
          Zoom out to view non-contiguous states (Alaska and Hawai’i). Policy
          counts are the default; user has option to track state TBED policy
          activity by Topic (human capital, innovation, physical infrastructure)
          and Context (higher education, industry, nonprofit, partnership,
          public, and secondary education). Darker blue represents more annual
          policies along the user-specified grouping; lighter blue represents
          fewer annual policies. Hover over the state to see the actual counts
          for each user-specified grouping.{' '}
        </p>
        <h3>Cumulative Policy Counts</h3>
        <figure>
          <TableauVisualization
            id="viz1591753602107"
            path="CumulativeStatePolicyCountsoverTime2/Dashboard1"
            placeholderImagePath="Cu/CumulativeStatePolicyCountsoverTime2/Dashboard1/1.png"
            width={1000}
            height={827}
            minHeight={727}
            isMobile={isMobile}
            loadOrder={2}
          />
        </figure>
        <p>
          The default is total policies from 2000–2015, but the user can specify
          different years to see the cumulative totals from 2000 up to the
          specified year. Zoom out to view non-contiguous states (Alaska and
          Hawai’i). Total policy counts are the default; user has option to
          track state TBED policy activity by Topic (human capital, innovation,
          physical infrastructure) and Context (higher education, industry,
          nonprofit, partnership, public, and secondary education). Darker blue
          represents more cumulative policies along the user-specified grouping
          and years; lighter blue represents fewer cumulative policies. Hover
          over the state to see the actual counts for each user-specified
          grouping.
        </p>
        <h3>Concentration in Topic and Context Over Time</h3>
        <figure>
          <TableauVisualization
            id="viz1591837169917"
            path="StatePortfolioSpread/Dashboard2"
            placeholderImagePath="St/StatePortfolioSpread/Dashboard2/1.png"
            width={1050}
            height={1227}
            minHeight={877}
            isMobile={isMobile}
            loadOrder={3}
          />
        </figure>
        <p>
          We delineate three temporal shifts tracing five (to six) years of
          activity to reflect broader trends in the data. We estimate the level
          of concentration based on the depth (blue) or breadth (red) of policy
          activity. The value 0 is centered at the median.
        </p>
        <p>
          For example, a state will have deep (blue) concentration in Context if
          they direct policy activity more narrowly towards one of six options
          (e.g., what sector—public, private, higher or secondary education,
          non-profit, or partnership across two or more of these sectors).
          Conversely, a state will have broad (red) concentration in Context if
          they direct policy activity evenly across the six options of public,
          private, higher or secondary education, non-profit, or partnership
          across two or more of these sectors. We identify the state’s
          archetype, which is a function of their concentration across Topic and
          Context.
        </p>
        <p>
          We list all archetypes as the default. User has option to select the
          set of states based on their archetype. Hover over the state to see
          actual topic and context concentration levels.
        </p>
        <h3>States by Archetypes</h3>
        <figure id="figure-count">
          <img
            src={graphicUrl}
            alt="A map of the United States with color-coded states for the four archetypes: ecosystem designers, hub specialists, industry architects, and public entrepreneurs."
          />
        </figure>
        <p>
          Hub specialists, public entrepreneurs, industry architects, and
          ecosystem designers—based on their cumulative topic and context
          concentration from 2000–2015. Figure 3 in the paper shows where each
          state lies along the topic and context concentration spectrum.
        </p>
      </div>
      <footer className="Article-footer container">
        <p>
          To explore the raw data underlying this work and subsequent
          visualizations, visit the{' '}
          <a href="https://www.cmu.edu/epp/stategovexp/">
            CMU State Goverment Data webpage
          </a>
          . For more about technology-based economic development policies, read{' '}
          <a href={ArticleDetails.articleLink}>the full paper</a>.
        </p>
      </footer>
    </article>
  );
}
